import React from 'react';
import { fromPairs, map, get, isEmpty, isEqual, filter, find } from 'lodash';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { checkbox as CheckBox } from 'ui/views/generic/checkbox';
import { GLOBAL_LABELS, ADDRESS_SELECTION_TYPE } from 'constants/common';
import { popover } from 'modules/popover';
import classnames from 'classnames';


const AddressSelectionOption = ( props ) => {
    const{ address, addressType, selectionType, isSelected } = props;

    const addressTitle = find( addressType, { value: address.type } );

    // get display title
    const displayAddress = filter( [ address.line1, address.line2, address.city, address.state, address.zip ], Boolean ).join( ', ' );

    return(
        <div className='ui-address-selection-view'>
            <div className='ui-address-selection-view__head' onClick={ () => props.onSelectAddressClick( { selectionType, address } ) }>
                <CheckBox
                    key={ address.type }
                    size='xs'
                    id={ address.type }
                    value={ isSelected }
                    className={ 'ui-address-selection-view__head__checkbox'}
                />
                { getLabel( addressTitle.title ) }
            </div>

            <div className='i-address-selection-view__body'>
                { displayAddress }
            </div>
           
        </div>
    );
};

/************************************************** */

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { addEditAddressForm as AddEditAddressForm } from 'ui/views/widgets/addEditAddressForm';
 *
 * // render
 * <AddEditAddressForm propName='propValue'/>
 */
export const addEditAddressForm = ( props ) => {
    // get valid action types
    const actions = filter( props.actions, ( action ) => {
        if( 'add' === props.actionType ) {
            return 'cancel' === action.name || 'save' === action.name;
        } else{
            return  'cancel' === action.name || 'update' === action.name;
        }
    } );

    const selectionAction = filter( props.actions, ( action ) => {
        return 'cancel' === action.name || 'select' === action.name;
    } );

    // selection mode data
    const { addressSelectionConfig, addressOptions, selected = {} } = props;

    return (
        <div className='ui-add-edit-address-form'>

            <div className='ui-add-edit-address-form__header'>

                {/* title for the modal */}
                <div className='ui-add-edit-address-form__header__title'>
                    { 'add' === props.actionType ? getLabel( props.addTitle ) : getLabel( props.editTitle ) }
                </div>

                {/* close button */}
                <div className='ui-error-overlay__head__close' onClick={ () => props.closeOverlay() }>
                    <Icon name='close' width='14' height='14' fill='#ffffff' />
                </div>

            </div>

            <div className='ui-add-edit-address-form__body'>

                {/* selection mode */}
                { 'selection' === props.mode ?  (
                    <div className='ui-add-edit-address-form__body__selection'>

                        <div className='ui-add-edit-address-form__body__selection__message'>
                            { getLabel( addressSelectionConfig.description.title ) }
                        </div>

                        <div className='ui-add-edit-address-form__body__selection__recommended'>
                            <div className='ui-add-edit-address-form__body__selection__recommended__title'>{ getLabel( addressSelectionConfig.recommendedAddres.title ) }</div>
                            
                            {
                                isEmpty( addressOptions.recommended ) ? (
                                    <div className='ui-add-edit-address-form__body__selection__recommended__no-data'>{ getLabel( GLOBAL_LABELS.DISPLAY_TEXT_NO_MATCH_FOUND ) }</div>
                                ) : map( addressOptions.recommended, address => {
                                    const isSelected = ! isEmpty( selected.address ) && isEqual( selected.address, address ) && isEqual( selected.selectionType, ADDRESS_SELECTION_TYPE.RECOMMENDED );
                                    return (
                                       <AddressSelectionOption
                                            key={ address.type }
                                            address= { address }
                                            addressType={ addressOptions.addressType }
                                            selectionType={ ADDRESS_SELECTION_TYPE.RECOMMENDED }
                                            onSelectAddressClick={ props.onSelectAddressClick }
                                            isSelected={ isSelected }
                                        />
                                    );
                                } )
                            }
                        </div>

                        <div className='ui-add-edit-address-form__body__selection__other'>
                            <div className='ui-add-edit-address-form__body__selection__other__title'>{ getLabel( addressSelectionConfig.yourSelection.title ) }</div>
                            <AddressSelectionOption
                                address= { addressOptions.submitted }
                                addressType={ addressOptions.addressType }
                                selectionType={ ADDRESS_SELECTION_TYPE.SUBMITTED }
                                isSelected= { ! isEmpty( selected.address ) && isEqual( selected.address, addressOptions.submitted ) && isEqual( selected.selectionType, ADDRESS_SELECTION_TYPE.SUBMITTED ) }
                                onSelectAddressClick={ props.onSelectAddressClick }
                            />
                        </div>

                        <div className='ui-add-edit-address-form__body__selection__actions'>
                            { map( selectionAction, action => {
                                return (
                                    <Button
                                        key={ action.name }
                                        id= { action.name }
                                        type= { 'button' }
                                        disabled={ 'cancel' !== action.name && isEmpty( selected ) ? true : false }
                                        { ...action }
                                        outline={ 'cancel' === action.name ? true: false }
                                        size='medium'
                                        className='ui-add-edit-address-form__body__selection__actions__action'
                                        onClick = { isEqual( 'cancel', action.name ) ?  () => props.onCancelClick() : () => props.onSumbitClick( { formData: selected.address } ) }
                                    />
                                );
                            } ) }
                        </div>
                    </div>

                ): (

                    <Form
                        fields={ fromPairs( map( props.fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
                        values={ props.itemData } // default values
                        isValid={ false }
                        callingfrom="addressAddEdit"
                        mode={ props.mode }
                        onSubmit={ ( { values, isValid } ) => {
                            if( isValid ) {
                                props.onFormSubmit( {
                                    id: ! isEmpty( props.itemData ) ? props.itemData.id : null,
                                    city: values.city,
                                    line1: values.line1,
                                    line2: values.line2,
                                    state: values.state,
                                    type: values.type,
                                    zip: values.zip,
                                    poBox: values.poBox,
                                    //disabled for sprint 37
                                   defaultBillAddress: props.itemData.defaultBillAddress,
                                    defaultShipAddress: props.itemData.defaultShipAddress
                                } );
                            }
                        } }
                    >
                        { ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {

                            return (
                                <form
                                    onSubmit={ onSumbit }
                                    className='ui-add-edit-address-form__body__form'
                                >
                                    { /* fields */ }
                                    { map( fields, ( field ) => {

                                        const fieldClassName = classnames( 'ui-add-edit-address-form__body__form__field', `ui-add-edit-address-form__body__form__${field.name}` );
                                        
                                        return (
                                            <field.view
                                                className={ fieldClassName }
                                                key={ field.name }
                                                name={ field.name }
                                                required={ field.required }
                                                placeholder={ field.placeholder }
                                                label={ field.title }
                                                onChange={ onChange }
                                                value={ values[ field.name ] }
                                                error={ errors[ field.name ] }
                                                options={ get( props.values.options, field.options ) }
                                                allowFilter={ 'state' === field.name }
                                                config={ field.config }
                                            />
                                        );
                                    } ) }

                                    {/* button actions */}
                                    <div className='ui-add-edit-address-form__body__form__actions'>
                                        { map( actions, action => {
                                            return (
                                                <Button
                                                    key={ action.name }
                                                    id= { action.name }
                                                    type= { 'cancel' !== action.name ? 'submit' : 'button' }
                                                    disabled={ 'cancel' !== action.name ? !isValid : false }
                                                    { ...action }
                                                    outline={ 'cancel' === action.name ? true: false }
                                                    size='medium'
                                                    className='ui-add-edit-address-form__body__form__actions__action'
                                                    onClick = { isEqual( 'cancel', action.name ) ?  () => {
                                                        popover.close();
                                                        props.onCancelClick();
                                                    } : null }
                                                />
                                            );
                                        } ) }
                                    </div>
                                </form>
                            );
                        } }
                    </Form>
                ) }
				
			</div>
        </div>
    );
};

// set display name
addEditAddressForm.displayName = 'addEditAddressForm';

// set default props
addEditAddressForm.defaultProps = {};

// prop types
addEditAddressForm.propTypes = {};
